import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { Async } from "react-async";
import { useNavigate, useParams } from "react-router-dom";
import { Button, ButtonTypes } from "../../../../components/ui/Button";
import { Icon, IconSymbols } from "../../../../components/ui/Icon";
import { Panel } from "../../../../components/ui/Panel";
import { PositionedSpinner } from "../../../../components/ui/PositionedSpinner";
import { ProgressStatus } from "../../../../components/ui/ProgressStatus";
import { RagStatus } from "../../../../components/ui/RagStatus";
import { ReviewButton } from "../../../../components/ui/ReviewButton";
import { TabGroup } from "../../../../components/ui/TabGroup";
import { CanEdit } from "../../../../contexts/permissions/PermissionHelpers";
import { PermissionFields } from "../../../../contexts/permissions/PermissionsTypes";
import { useFlightPathUser } from "../../../../core/auth_insight";
import I18n from "../../../../core/localization/I18n";
import { Animations } from "../../../../core/util/Animations";
import { isItemComplete } from "../../../../core/util/ReviewHelpers";
import { Enums } from "../../../../enums";
import { useCurrentOrganisation } from "../../../../services/local/organisationContext/OrganisationContextModel";
import { CommentListView } from "../../comments/commentListView/CommentListView_view";
import { RevisionHistoryView } from "../../revisionHistory/RevisionHistory_view";
import { ActionDisplay } from "../ActionDisplay";
import { ActionImpacts } from "../actionImpacts/ActionImpacts_view";
import { ActionExtendedViewModel } from "./ActionExtendedView_model";
import Pages from "../../../../routes/InsightRoutes";

export interface ActionExtendedViewProps {}

const ActionExtendedView: React.FunctionComponent<ActionExtendedViewProps> = observer(() => {
  const authUser = useFlightPathUser();
  const navigate = useNavigate();
  const organisation = useCurrentOrganisation();
  const { actionId, projectId } = useParams<{ projectId: string; actionId: string }>();
  const [model] = useState(
    () =>
      new ActionExtendedViewModel({
        authUser,
        organisationId: organisation.id,
        actionId: +actionId,
        projectId: +projectId
      })
  );
  const hasBeenReviewed = model.hasBeenReviewed;

  const { action } = model;

  useEffect(() => {
    model.onMount();
    return model.onUnmount;
  }, [model]);

  const load = useCallback(async () => {
    await model.loadAction();
  }, [model]);

  return (
    <Async
      promiseFn={load}
      onReject={err => {
        return navigate(Pages.projects.actions.listView.generateLink(+organisation.id, +projectId));
      }}
    >
      <Async.Loading>
        <PositionedSpinner />
      </Async.Loading>
      <Async.Resolved>
        {() => (
          <div className="action-extended-view data-extended-view pt-6">
            <div className="container-fluid data-extended-view__block data-extended-view__block--header mb-5">
              <div className="row">
                <div className="col m-0 p-0">
                  <Button
                    size={Enums.UiSizes.MD}
                    type={ButtonTypes.LINK}
                    className="p-0 ml-2"
                    onClick={() => navigate(-1)}
                  >
                    <Icon className="mr-2" symbol={IconSymbols.ChevronLeft} />
                    {I18n.t("phrases.back")}
                  </Button>
                </div>
              </div>
            </div>
            <div className="data-extended-view__block data-extended-view__block--wrapper">
              <div className={`data-extended-view__col container-fluid ${Animations.FP_ZOOM_IN} speed-5`}>
                <CanEdit field={PermissionFields.ACTIONS} projectId={model.projectId}>
                  <div className="row mb-5">
                    <div className="col">
                      <ReviewButton
                        isOutOfAction={isItemComplete(action.progressStatus)}
                        hasBeenReviewed={hasBeenReviewed}
                        onClick={model.showMarkReviewedModal}
                      />
                    </div>
                  </div>
                </CanEdit>

                <div className="row mb-4">
                  <div className="col">
                    <div className="d-flex">
                      <h2 className="mb-0" id="ActionNameLabel">
                        {action.name} | {I18n.t("phrases.details")}
                      </h2>
                    </div>
                    <h3 className="d-flex align-items-center">
                      <span className="mr-2 h3-heading">{action.refNumber}</span>
                      {action.progressStatus === Enums.ProgressStatus.COMPLETED ? (
                        <ProgressStatus state={action.progressStatus} className="mr-2" />
                      ) : (
                        <RagStatus state={action.ragStatus} className="mr-2" />
                      )}
                    </h3>
                  </div>
                </div>
                <ActionDisplay item={action} />
              </div>
              <Panel.Panel
                background={Panel.PanelBackgrounds.BG_LIGHT}
                className={`data-extended-view__col data-extended-view__col--tabs ${Animations.FP_ZOOM_IN} speed-5`}
              >
                <TabGroup
                  className="data-extended-view__tabs"
                  tabs={[
                    {
                      id: "ImpactsTab",
                      title: <h3 className="mb-0 text-center">{I18n.t("phrases.impacts")}</h3>,
                      children: <ActionImpacts model={model.actionImpactsModel} />
                    },
                    {
                      id: "NotesTab",
                      title: <h3 className="mb-0 text-center">{I18n.t("phrases.notes")}</h3>,
                      children: (
                        <div className="col">
                          <CommentListView model={model.commentViewModel} field={PermissionFields.ACTIONS} />
                        </div>
                      )
                    },
                    {
                      id: "HistoryTab",
                      title: <h3 className="mb-0 text-center">{I18n.t("phrases.actionHistory")}</h3>,
                      children: <RevisionHistoryView model={model.revisionHistoryModel} />
                    }
                  ]}
                ></TabGroup>
              </Panel.Panel>
            </div>
          </div>
        )}
      </Async.Resolved>
    </Async>
  );
});

export { ActionExtendedView };
